<template>
  <div id="app">
    <b-modal
      no-fade
      id="modal-scrollable"
      size="xl"
      ok-only
      title="About Flettner rotor savings estimator"
    >
      <h5>Usage</h5>
      <ul>
        <li>Select ship type</li>
        <li>Customize vessel particulars (optional)</li>
        <li>Select rotor size and position</li>
        <li>Click calculate after changing rotors (may take several seconds to process)</li>
        <li>Pick route</li>
        <li>
          Customize route
          <ul>
            <li>Drag existing waypoints</li>
            <li>Click existing waypoint to delete</li>
            <li>Click on map to insert new waypoint at the end of route</li>
            <li>Click on route to insert new intermadiate waypoint</li>
          </ul>
        </li>
        <li>Map can be panned and zoomed using the mouse</li>
      </ul>
      <p>
        <small>
          <a
            href="https://lretiwebappstorage.blob.core.windows.net/frs-tool/FRStool-win32-x64.zip"
          >download stand alone version</a>
        </small>
      </p>
    </b-modal>
    <LandingPage v-show="!display.accepted" @onTermsAccepted="handleTermsAccepted" />
    <div v-show="display.accepted">
      <nav class="navbar navbar-expand-lg navbar-light bg-light">
        <a class="navbar-brand" href="#" @click="display.accepted=false">
          <img src="./assets/lr_logo.svg" height="50" class="d-inline-block align-middle" alt />
          <span
            class="underline"
            style="font-size:2rem;font-weight:bold;margin-left:40px;"
          >Flettner rotor savings estimator.</span>
        </a>
        <button
          class="navbar-toggler"
          type="button"
          data-toggle="collapse"
          data-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <div class="collapse navbar-collapse" id="navbarSupportedContent">
          <ul class="navbar-nav ml-auto">
            <li class="nav-item">
              <a v-b-modal.modal-xl.modal-scrollable class="nav-link" href="#" role="button">About</a>
            </li>
          </ul>
        </div>
      </nav>

      <ul class="nav nav-tabs d-print-none">
        <li class="nav-item">
          <a
            :class="`nav-link ${display.tab === 0 ? 'active' : ''} ${display.maxTab < 0 ? 'disabled' : ''}`"
            href="#"
            v-on:click="display.tab=0;"
          >Vessel</a>
        </li>
        <li class="nav-item">
          <a
            :class="`nav-link ${display.tab === 1 ? 'active' : ''} ${!shipSelected ? 'disabled' : ''}`"
            href="#"
            v-on:click="display.tab=1;"
          >Rotors and route</a>
        </li>
        <li class="nav-item">
          <a
            :class="`nav-link ${display.tab === 2 ? 'active' : ''} ${display.maxTab < 2 ? 'disabled' : ''}`"
            href="#"
            v-on:click="onSaveProject();"
          >Print summary</a>
        </li>
      </ul>
      <div class="container-fluid fill">
        <div v-show="display.tab === 0">
          <ShipSelector v-if="display.step==-1" :ships="ships" @onSelectShip="handleSelectShip" />
          <div v-if="display.step>=0" class="row">
            <div class="col-6">
              <button @click="display.step=-1">Change vessel</button>
              <ShipView
                :ship="project.ship"
                :view="'profile'"
                :rotorsEditable="!results.calculating"
                style="width:100%"
              />
              <Chart
                style="width:50%;height:25%"
                v-if="display.step > 1"
                :curves="speedPowerCurves"
                xTitle="Ship speed (knots)"
                yTitle="Power (kW)"
                :resizeTrigger="display.tab+display.step"
              />
              <Chart
                style="width:50%;height:25%"
                v-if="display.step > 3"
                :curves="sfocCurves"
                xTitle="Engine load (%)"
                yTitle="SFOC (g/kW/h)"
                :xMultiplier="100"
                :resizeTrigger="display.tab+display.step"
              />
              <Chart
                style="width:50%;height:25%"
                v-if="display.step > 2"
                :curves="propellerCurves"
                xTitle="J (-)"
                yTitle="Kt,10Kq,Eta0 (-)"
                :resizeTrigger="display.tab+display.step"
              />
            </div>
            <div class="col-6">
              <ship-editor
                :ship="project.ship"
                :step="display.step"
                @onStepChanged="handleStepChange"
                @onShipTypeChanged="handleShipTypeChange"
              />
            </div>
          </div>
        </div>
        <div v-show="display.tab === 1" class="row h-100">
          <div class="col-6 h-100 d-flex flex-column">
            <div
              class
              style="background: #ffffff;position: absolute;top: 0px;right: 15px;z-index: 10; padding: 2px;width:80%"
            >
              <div class="form-row">
                <label class="col-1 col-form-label col-form-label-sm">Wind for:</label>
                <div class="col-3">
                  <select class="form-control form-control-sm" v-model.number="project.route.month">
                    <option value="0">January</option>
                    <option value="1">February</option>
                    <option value="2">March</option>
                    <option value="3">April</option>
                    <option value="4">May</option>
                    <option value="5">June</option>
                    <option value="6">July</option>
                    <option value="7">August</option>
                    <option value="8">September</option>
                    <option value="9">October</option>
                    <option value="10">November</option>
                    <option value="11">December</option>
                  </select>
                </div>
                <div class="col-1"></div>
                <label class="col-1 col-form-label col-form-label-sm">Route:</label>
                <div class="col-3">
                  <select
                    class="form-control form-control-sm"
                    :value="project.route.selectedRoute"
                    v-on:change="routeSelect"
                  >
                    <option v-if="project.route.selectedRoute === 'Customised'" hidden>Customised</option>
                    <option v-for="(route, index) in routes" :value="index">{{ index }}</option>
                  </select>
                </div>
                <div class="col-3 text-left">
                  <button class v-on:click="handleReverse">reverse</button>
                  <button
                    class
                    v-on:click="project.route.waypoints = []; project.route.selectedRoute = ''"
                  >clear</button>
                </div>
              </div>
            </div>
            <Map
              v-bind:waypoints="project.route.waypoints"
              v-bind:segments="segments"
              v-bind:windRange="windRange"
              v-bind:month="project.route.month"
              @onRouteChange="handleRouteChange"
            />
            <h5>Savings along route for {{monthsFull[project.route.month]}}:</h5>
            <div class="d-flex flex-fill">
              <Chart
                :curves="segments"
                xKey="distMid"
                yKey="savings"
                xTitle="Distance (NM)"
                yTitle="Savings (%)"
                :yMultiplier="100"
                :resizeTrigger="display.tab+display.step"
              />
            </div>
          </div>
          <div class="col-6">
            <div class="row">
              <div class="col-6">
                <h5>Rotor size selection (diameter x height)</h5>
                <div class="row">
                  <template v-for="rotors in rotorSizes">
                    <!--<div class="col-3" ">-->
                    <button
                      class="rotor-button"
                      :class="{active : (rotor.D == project.rotors.D && rotor.h==project.rotors.h)}"
                      :disabled="results.calculating"
                      @click="project.rotors.D=rotor.D;project.rotors.h=rotor.h;handleFlettner()"
                      v-for="rotor in rotors"
                    >{{rotor.D}}x{{rotor.h}}</button>
                    <!--</div>-->
                  </template>
                </div>

                <!--<div class="form-group row">
                  <label for="h_rotor" class="col-sm-4 col-form-label">Rotor height (m)</label>
                  <div class="col-sm-3">
                    <input
                      class="form-control"
                      type="number"
                      min="5"
                      max="40"
                      step="1"
                      v-model.number="project.rotors.h"
                      @change="results.performance = null"
                    />
                  </div>
                  <div class="col-sm-3 col-form-label">(5-40)</div>
                </div>
                <div class="form-group row">
                  <label for="d_rotor" class="col-sm-4 col-form-label">Rotor diameter (m)</label>
                  <div class="col-sm-3">
                    <input
                      class="form-control"
                      type="number"
                      min="1"
                      max="8"
                      step="1"
                      v-model.number="project.rotors.D"
                      @change="results.performance = null"
                    />
                  </div>
                  <div class="col-sm-3 col-form-label">(1-8)</div>
                </div>-->
              </div>
              <div class="col-6">
                <h5>Rotor position selection</h5>
                <ShipView
                  v-bind:ship="project.ship"
                  v-bind:view="'profile'"
                  :rotors="project.rotors"
                  @onRotorChange="handleFlettner"
                />
              </div>
            </div>
            <div class="form-group row">
              <div class="col-3 text-right" style="padding-top:3px">Loading condition:</div>
              <div class="col-7">
                <div class="row">
                  <button
                    v-for="draught in shipDraughts"
                    class="rotor-button"
                    :class="{active : (draught.T == project.route.draught)}"
                    :disabled="results.calculating"
                    @click="project.route.draught=draught.T;handleFlettner()"
                  >
                    {{draught.descr}}
                    <br />
                    T: {{draught.T.toFixed(1)}} m
                  </button>
                </div>
              </div>
            </div>

            <!--<FormRangeSlider
              id="Tloading"
              tag="T (m)"
              descr="Draught at loading condition"
              v-bind:value="project.route.draught"
              v-on:update:value="project.route.draught = $event"
              :vMin="Math.ceil(project.ship.particulars.Tballast*2)/2"
              :vMax="Math.floor(project.ship.particulars.T*2)/2"
              :step="0.5"
              @change="results.performance = null"
            />
            <button
              v-on:click="handleFlettner"
              :disabled="results.performance || results.calculating"
            >Calculate!</button>
            <br />-->
            <FormRangeSlider
              id="Vs"
              tag="V<sub>s</sub> (knots)"
              descr="Ship speed"
              v-bind:value="project.route.speed"
              v-on:update:value="project.route.speed = $event"
              :vMin="Math.ceil(0.5*project.ship.particulars.Vmax*2)/2"
              :vMax="Math.floor(project.ship.particulars.Vmax*2)/2"
              :step="0.5"
            />
            <!-- <div v-if="(results.calculating)">
              Calculating
              <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
              </div>
            </div>-->

            <div class="row">
              <div class="col-md-6">
                <div v-if="(results.calculating)">
                  Calculating
                  <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                </div>
                <div v-else>
                  <h4>Performance polar:</h4>
                  <polar-plot :performance="savingsAtSpeed" />
                </div>
              </div>
              <div class="col-md-6">
                <div v-if="results.performance === null" class="alert alert-warning">
                  <h5>No performance results</h5>
                  <ul class="text-left">
                    <li>Click calculate</li>
                    <li>Wait for calculation to finish</li>
                  </ul>
                </div>
                <div v-if="project.route.waypoints.length < 2" class="alert alert-warning">
                  <h5>please select a route</h5>
                  <ul class="text-left">
                    <li>select from the dropdown in the top right of the map</li>
                    <li>create/edit manually</li>
                    <ul>
                      <li>clicking on the map will insert a waypoint</li>
                      <li>clicking on an existing waypoints will remove it</li>
                      <li>waypoints can be moved by dragging them</li>
                    </ul>
                  </ul>
                </div>

                <div v-if="(project.route.waypoints.length > 1) && (results.performance !== null)">
                  <h4>Selected Route Results</h4>
                  <div class="text-left">
                    Distance: {{results.distance.toFixed(1)}} NM
                    <br />
                    Duration: {{(results.distance/project.route.speed/24).toFixed(1)}} days
                    <br />
                    Avg Speed: {{project.route.speed.toFixed(1)}} knots
                    <br />Monthly savings in propulsion consumption and emissions
                  </div>
                  <MonthlyPerformanceTable
                    :results.sync="results"
                    :month.sync="project.route.month"
                  />
                </div>
                <div v-if="results.performance !== null" class="text-left">
                  <h5>
                    Savings in global wind conditions*:
                    <strong>{{globalSavings}}</strong>
                  </h5>
                  <small>*yearly average wind conditions over all major trading routes as defined in MEPC 62/INF.34</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-show="display.tab === 2" class="container">
          <img src="./assets/lr_logo.svg" height="50" class="d-inline-block align-middle" alt />
          <span
            style="font-size:2rem;font-weight:bold;margin-left:40px;border-bottom: 5px solid #3b8ede"
          >Flettner rotor savings estimator.</span>
          <h4 style="text-align:right;margin: 10px 0">Results summary: {{href()}}</h4>
          <div class="row">
            <div class="col-4">
              <h5>Vessel details</h5>
              <ShipView
                v-bind:ship="project.ship"
                v-bind:view="'profile'"
                :rotors="project.rotors"
                :rotorsEditable="false"
              />
              <table style="margin: 0 auto;">
                <thead>
                  <tr>
                    <td>
                      <strong>Particulars</strong>
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      L
                      <sub>OA</sub>
                    </td>
                    <td>{{project.ship.particulars.Loa}}</td>
                    <td>m</td>
                  </tr>
                  <tr>
                    <td>
                      L
                      <sub>PP</sub>
                    </td>
                    <td>{{project.ship.particulars.Lpp}}</td>
                    <td>m</td>
                  </tr>
                  <tr>
                    <td>
                      L
                      <sub>WL</sub>
                    </td>
                    <td>{{project.ship.particulars.Lwl}}</td>
                    <td>m</td>
                  </tr>
                  <tr>
                    <td>B (m)</td>
                    <td>{{project.ship.particulars.B}}</td>
                    <td>m</td>
                  </tr>
                  <tr>
                    <td>T</td>
                    <td>{{project.ship.particulars.T}}</td>
                    <td>m</td>
                  </tr>
                  <tr>
                    <td>D</td>
                    <td>{{project.ship.particulars.D}}</td>
                    <td>m</td>
                  </tr>
                  <tr>
                    <td>
                      C
                      <sub>b</sub>
                    </td>
                    <td>{{project.ship.particulars.Cb}}</td>
                    <td>m</td>
                  </tr>
                  <tr>
                    <td>
                      P
                      <sub>ME</sub> total
                    </td>
                    <td>{{project.ship.particulars.Pme}}</td>
                    <td>kW</td>
                  </tr>
                  <tr>
                    <td>
                      D
                      <sub>prop</sub>
                    </td>
                    <td>{{project.ship.propeller.D}}</td>
                    <td>m</td>
                  </tr>
                  <tr>
                    <td>
                      N
                      <sub>shaft</sub>
                    </td>
                    <td>{{project.ship.propeller.N}}</td>
                    <td>-</td>
                  </tr>
                </tbody>
              </table>
              <div class="row" style="height:250px">
                <Chart
                  :curves="speedPowerCurves"
                  xTitle="Ship speed (knots)"
                  yTitle="Power (kW)"
                  :resizeTrigger="display.tab+display.step"
                />
              </div>
              <div class="row" style="height:250px">
                <Chart
                  :curves="propellerCurves"
                  xTitle="J (-)"
                  yTitle="Kt,10Kq,Eta0 (-)"
                  :resizeTrigger="display.tab+display.step"
                />
              </div>
              <div class="row" style="height:250px">
                <Chart
                  :curves="sfocCurves"
                  xTitle="Engine load (%)"
                  yTitle="SFOC (g/kW/h)"
                  :xMultiplier="100"
                  :resizeTrigger="display.tab+display.step"
                />
              </div>
            </div>
            <div class="col-8">
              <h5>Route</h5>
              <Map
                v-bind:waypoints="project.route.waypoints"
                v-bind:segments="segments"
                v-bind:windRange="windRange"
                v-bind:month="project.route.month"
                :colorRoute="false"
                :showWind="false"
                :interactive="false"
              />
              <h4>FRS performance</h4>

              <div class="row">
                <div class="col-md-6">
                  <h4>Performance polar:</h4>
                  <polar-plot :performance="savingsAtSpeed" />
                </div>
                <div class="col-md-6">
                  <div
                    v-if="(project.route.waypoints.length > 1) && (results.performance !== null)"
                  >
                    <h4>Selected Route Results</h4>
                    <div class="text-left">
                      Distance: {{results.distance.toFixed(1)}} NM
                      <br />
                      Duration: {{(results.distance/project.route.speed/24).toFixed(1)}} days
                      <br />
                      Avg Speed: {{project.route.speed.toFixed(1)}} knots
                      <br />Monthly savings in propulsion consumption and emissions
                    </div>
                    <MonthlyPerformanceTable
                      :results.sync="results"
                      :month.sync="project.route.month"
                    />
                  </div>
                  <div v-if="results.performance !== null" class="text-left">
                    <h5>
                      Savings in global wind conditions*:
                      <strong>{{globalSavings}}</strong>
                    </h5>
                    <small>**yearly average wind conditions over all major trading routes as defined in MEPC 62/INF.34</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const API_URL = process.env.NODE_ENV === "production" ? "/api/" : "/";

import axios from "axios";
import routeCalculator from "./assets/RouteCalculator.js";
import Map from "./components/Map.vue";
import Chart from "./components/Chart.vue";
import Waypoints from "./components/Waypoints.vue";
import ShipView from "./components/ShipView.vue";
import ShipPlan from "./components/ShipPlan.vue";
import RouteChart from "./components/RouteChart.vue";
import ShipEditor from "./components/ShipEditor.vue";
import PolarPlot from "./components/PolarPlot.vue";
import colorMap from "./assets/ColorMap.js";
import FormRangeSlider from "./components/FormRangeSlider.vue";
import LandingPage from "./components/LandingPage.vue";
import ShipSelector from "./components/ShipSelector.vue";
import MonthlyPerformanceTable from "./components/MonthlyPerformanceTable.vue";
import ships from "./components/ships.js";
import * as d3 from "d3";
import Spline from "cubic-spline";

const tanker = ships.tanker1;
const bulkCarrier = ships.bulkCarrier1;

const noSavings = [
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
];

const globalWind = [
  [
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018,
    0.0018
  ],
  [
    0.0066,
    0.0064,
    0.006,
    0.0057,
    0.0057,
    0.0055,
    0.0055,
    0.0057,
    0.0057,
    0.006,
    0.0066,
    0.0066,
    0.0066,
    0.0064,
    0.006,
    0.0057,
    0.0057,
    0.0055,
    0.0055,
    0.0057,
    0.0057,
    0.006,
    0.0066,
    0.0066
  ],
  [
    0.0113,
    0.0106,
    0.0096,
    0.0087,
    0.008,
    0.0077,
    0.008,
    0.0082,
    0.0087,
    0.0098,
    0.0109,
    0.0114,
    0.0113,
    0.0106,
    0.0096,
    0.0087,
    0.008,
    0.0077,
    0.008,
    0.0082,
    0.0087,
    0.0098,
    0.0109,
    0.0114
  ],
  [
    0.0127,
    0.0119,
    0.0105,
    0.0093,
    0.0082,
    0.0081,
    0.0081,
    0.0086,
    0.0091,
    0.0104,
    0.0119,
    0.0129,
    0.0127,
    0.0119,
    0.0105,
    0.0093,
    0.0082,
    0.0081,
    0.0081,
    0.0086,
    0.0091,
    0.0104,
    0.0119,
    0.0129
  ],
  [
    0.009,
    0.0088,
    0.0082,
    0.0072,
    0.0064,
    0.006,
    0.0059,
    0.0059,
    0.0063,
    0.0072,
    0.0082,
    0.0088,
    0.009,
    0.0088,
    0.0082,
    0.0072,
    0.0064,
    0.006,
    0.0059,
    0.0059,
    0.0063,
    0.0072,
    0.0082,
    0.0088
  ],
  [
    0.0051,
    0.005,
    0.0045,
    0.0043,
    0.0038,
    0.0036,
    0.003,
    0.003,
    0.003,
    0.0035,
    0.004,
    0.0045,
    0.0051,
    0.005,
    0.0045,
    0.0043,
    0.0038,
    0.0036,
    0.003,
    0.003,
    0.003,
    0.0035,
    0.004,
    0.0045
  ],
  [
    0.0022,
    0.0023,
    0.0021,
    0.0019,
    0.0015,
    0.0015,
    0.0015,
    0.0013,
    0.0014,
    0.0015,
    0.0017,
    0.0021,
    0.0022,
    0.0023,
    0.0021,
    0.0019,
    0.0015,
    0.0015,
    0.0015,
    0.0013,
    0.0014,
    0.0015,
    0.0017,
    0.0021
  ],
  [
    0.0009,
    0.0009,
    0.0009,
    0.0009,
    0.0007,
    0.0006,
    0.0006,
    0.0006,
    0.0006,
    0.0006,
    0.0009,
    0.0009,
    0.0009,
    0.0009,
    0.0009,
    0.0009,
    0.0007,
    0.0006,
    0.0006,
    0.0006,
    0.0006,
    0.0006,
    0.0009,
    0.0009
  ],
  [
    0.0006,
    0.0006,
    0.0005,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0004,
    0.0006,
    0.0006,
    0.0005,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0003,
    0.0004
  ],
  [
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0
  ]
];

var colors = d3
  .scaleThreshold()
  .domain([0.0, 0.1, 1.0])
  .range(["#f0c36a", "#00ea00", "#009d00"]);

// const NWINDSPEEDS = 10
// conts NWINDDIRECTIONS =

export default {
  name: "app",
  components: {
    Map,
    Waypoints,
    ShipView,
    ShipPlan,
    RouteChart,
    ShipEditor,
    PolarPlot,
    FormRangeSlider,
    MonthlyPerformanceTable,
    ShipSelector,
    Chart,
    LandingPage
  },
  data: function() {
    return {
      loadedProject: false,
      changedSinceSave: true,
      shipSelected: false,
      project: {
        ship: JSON.parse(JSON.stringify(tanker)),
        route: {
          selectedRoute: "",
          month: 0,
          waypoints: [],
          speed: 12.0,
          draught: 10
        },
        rotors: {
          h: 30.0,
          D: 5.0,
          placement: [
            [false, false, true, false],
            [false, false, false, false],
            [false, true, false, false]
          ]
        }
      },
      results: {
        performance: null,
        voyageSegments: [],
        savings: "0.0 %",
        distance: 0.0,
        monthlySavings: [],
        calculating: false
      },
      display: {
        tab: 0,
        step: -1,
        maxTab: 0,
        accepted: false
      },
      windRange: 0,
      routes: {
        "Rotterdam - New York": {
          waypoints: [
            { lon: 4.088715, lat: 51.948463 },
            { lon: 0.67, lat: 50.65 },
            { lon: -4.92515, lat: 49.466386 },
            { lon: -52.631465, lat: 45.903898 },
            { lon: -74.018004, lat: 40.537896 }
          ]
        },
        "Shanghai - Los Angeles": {
          waypoints: [
            { lon: 122.926287, lat: 31.064237 },
            { lon: 129.3, lat: 34.1 },
            { lon: 140.7, lat: 41.44 },
            { lon: 143.5, lat: 41.7 },
            { lon: -120, lat: 33.8 },
            { lon: -118.59752, lat: 33.780502 }
          ]
        },
        "Shanghai - Rotterdam": {
          waypoints: [
            { lon: 122.926287, lat: 31.064237 },
            { lon: 119.7, lat: 23 },
            { lon: 104.2, lat: 0.8 },
            { lon: 94.88, lat: 6.7 },
            { lon: 20, lat: -37 },
            { lon: -20, lat: 16 },
            { lon: -10, lat: 43.6 },
            { lon: -4.92515, lat: 49.466386 },
            { lon: 0.67, lat: 50.65 },
            { lon: 4.088715, lat: 51.948463 }
          ]
        },
        "Shanghai - Port Hedland": {
          waypoints: [
            { lon: 122.926287, lat: 31.064237 },
            { lon: 119.7, lat: 23 },
            { lon: 119.7, lat: 15.7 },
            { lon: 120.1, lat: 10.6 },
            { lon: 119.5, lat: 0 },
            { lon: 116.2, lat: -8.6 },
            { lon: 118.657183, lat: -20.267985 }
          ]
        },
        "Shanghai - Valencia": {
          waypoints: [
            { lon: 122.926287, lat: 31.064237 },
            { lon: 119.7, lat: 23 },
            { lon: 104.2, lat: 0.8 },
            { lon: 94.88, lat: 6.7 },
            { lon: 77.5, lat: 5.5 },
            { lon: 51.1, lat: 12.3 },
            { lon: 43.6, lat: 12.4 },
            { lon: 32.5, lat: 29.5 },
            { lon: 31.8, lat: 31.7 },
            { lon: 15, lat: 36.5 },
            { lon: -0.292857, lat: 39.425207 }
          ]
        },
        "Shanghai - Dubai": {
          waypoints: [
            { lon: 122.926287, lat: 31.064237 },
            { lon: 119.7, lat: 23 },
            { lon: 104.2, lat: 0.8 },
            { lon: 94.88, lat: 6.7 },
            { lon: 77.5, lat: 5.5 },
            { lon: 56.3, lat: 26.6 },
            { lon: 55.024452, lat: 25.223015 }
          ]
        }
      },
      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
      ],
      monthsFull: [
        "January",
        "February",
        "March",
        "Aprul",
        "May",
        "June",
        "July",
        "August",
        "September",
        "Octover",
        "November",
        "December"
      ]
    };
  },
  computed: {
    appMode: function() {
      return window.location.href.indexOf("index.html") > -1;
    },
    segments: function() {
      var res = routeCalculator(
        this.project.route.waypoints,
        this.$options.windData,
        this.performanceAtSpeed,
        this.project.route.month,
        this.windRange,
        this.sfocInterpolator(),
        this.project.ship.sfoc.aux,
        this.project.route.speed
      );
      this.results.savings = Math.round(res.totalSavings * 1000) / 10 + " %";
      this.results.distance = res.totalDistance;
      this.results.monthlySavings = res.monthlySavings;
      this.results.focMonthly = res.focMonthly;
      this.results.focYearly = res.focYearly;
      if (res.sections.length > 0 && this.results.performance) {
        this.display.maxTab = 2;
      } else {
        if (this.display.accepted) this.display.maxTab = 1;
      }
      return res.sections;
    },
    performanceAtSpeed: function() {
      if (!this.results.performance) return null;
      const normSpeed = Math.min(
        (this.project.route.speed - 0.5 * this.project.ship.particulars.Vmax) /
          (0.1 * this.project.ship.particulars.Vmax),
        4.999999
      );
      const idx = parseInt(normSpeed);
      console.log(normSpeed, idx);
      const x = normSpeed % 1.0;
      const x_ = 1 - x;

      var foc;
      var res = {
        power: [],
        powerFlettner: [],
        powerFlettnerOpt: [],
        powerFlettnerOptDev: [],
        rotorPower: []
      };
      for (let i = 0; i < this.results.performance.power[0].length; i++) {
        res.power.push([]);
        res.powerFlettner.push([]);
        res.powerFlettnerOpt.push([]);
        res.powerFlettnerOptDev.push([]);
        res.rotorPower.push([]);
        for (let j = 0; j < this.results.performance.power[0][0].length; j++) {
          res.power[i].push(
            x_ * this.results.performance.power[idx][i][j] +
              x * this.results.performance.power[idx + 1][i][j]
          );
          res.powerFlettner[i].push(
            x_ * this.results.performance.powerFlettner[idx][i][j] +
              x * this.results.performance.powerFlettner[idx + 1][i][j]
          );
          res.powerFlettnerOpt[i].push(
            x_ * this.results.performance.powerFlettnerOpt[idx][i][j] +
              x * this.results.performance.powerFlettnerOpt[idx + 1][i][j]
          );
          res.powerFlettnerOptDev[i].push(
            x_ * this.results.performance.powerFlettnerOptDev[idx][i][j] +
              x * this.results.performance.powerFlettnerOptDev[idx + 1][i][j]
          );
          res.rotorPower[i].push(
            x_ * this.results.performance.rotorPower[idx][i][j] +
              x * this.results.performance.rotorPower[idx + 1][i][j]
          );
        }
      }
      return res;
    },
    savingsAtSpeed: function() {
      if (!this.performanceAtSpeed) return null;
      const performance = this.performanceAtSpeed;
      const SFOCme = this.sfocInterpolator();
      var res = [];
      for (let ii = 0; ii < performance.power.length; ii++) {
        res.push([]);
        for (let ij = 0; ij < performance.power[0].length; ij++) {
          const focBL =
            SFOCme(performance.power[ii][ij]) * performance.power[ii][ij];
          const focFRS =
            SFOCme(performance.powerFlettner[ii][ij]) *
              performance.powerFlettner[ii][ij] +
            this.project.ship.sfoc.aux * performance.rotorPower[ii][ij];
          res[ii].push(1 - focFRS / focBL);
        }
      }
      return res;
    },
    speedPowerCurves: function() {
      return [this.project.ship.speedPower.map(x => [x.speed, x.power])];
    },
    propellerCurves: function() {
      return [
        this.project.ship.propeller.curves.map(x => [x.J, x.Kt]),
        this.project.ship.propeller.curves.map(x => [x.J, 10 * x.Kq]),
        this.project.ship.propeller.curves.map(x => [
          x.J,
          (x.J * x.Kt) / (2 * Math.PI * x.Kq)
        ])
      ];
    },
    sfocCurves: function() {
      return [this.project.ship.sfoc.mainEngine.map(x => [x.load, x.sfoc])];
    },
    sfoc: function() {
      if (this.project.ship.YOB <= 2000) {
        if (this.project.ship.Pme < 5000) return 225;
        if (this.project.ship.Pme < 15000) return 215;
        return 205;
      }
      if (this.project.ship.YOB <= 2000) {
        if (this.project.ship.Pme < 5000) return 205;
        if (this.project.ship.Pme < 15000) return 195;
        return 185;
      }
      if (this.project.ship.YOB <= 2007) {
        if (this.project.ship.Pme < 5000) return 195;
        if (this.project.ship.Pme < 15000) return 185;
        return 175;
      }
      if (this.project.ship.Pme < 5000) return 190;
      if (this.project.ship.Pme < 15000) return 180;
      return 170;
    },
    ships: function() {
      return Object.values(ships);
    },
    rotorSizes: function() {
      const Nstep = 5;
      const Dmin = 2;
      const Dmax = Math.min(this.project.ship.particulars.B / 6.4, 8);
      const Dstep = Math.max(
        Math.floor(((Dmax - Dmin) / (Nstep - 1)) * 2) / 2,
        0.5
      );
      var rotors = [];
      for (var i = 0; i < Nstep; i++) {
        if (Dmin + i * Dstep > Dmax) break;
        rotors.push([]);
        for (var ar = 4; ar <= 7; ar += 1) {
          rotors[i].push({ D: Dmin + i * Dstep, h: (Dmin + i * Dstep) * ar });
        }
      }
      return rotors;
    },
    shipDraughts: function() {
      const draughtstep =
        Math.floor(
          ((this.project.ship.particulars.T -
            this.project.ship.particulars.Tballast) /
            3) *
            10
        ) / 10;
      return [
        { descr: "Ballast", T: this.project.ship.particulars.Tballast },
        {
          descr: "Heavy ballast",
          T: this.project.ship.particulars.Tballast + draughtstep
        },
        { descr: "Loaded", T: this.project.ship.particulars.T - draughtstep },
        { descr: "Scantling", T: this.project.ship.particulars.T }
      ];
    },
    globalSavings: function() {
      if (this.savingsAtSpeed === null) return "-- %";
      var savings = 0;
      for (var i = 0; i < this.savingsAtSpeed.length; i++) {
        for (var j = 0; j < this.savingsAtSpeed[0].length - 1; j++) {
          savings += this.savingsAtSpeed[i][j] * globalWind[i][j];
          // debugger
        }
      }
      return (savings * 100).toFixed(1) + " %";
    }
  },
  methods: {
    href: function() {
      return this.appMode ? "" : window.location.href.replace("#", "");
    },
    sfocInterpolator: function() {
      if (this.project.ship.sfoc.mainEngine.length === 0)
        return function(P) {
          return null;
        };
      const x = this.project.ship.sfoc.mainEngine.map(x => x.load);
      const y = this.project.ship.sfoc.mainEngine.map(x => x.sfoc);
      const Pme = this.project.ship.particulars.Pme;
      const s = new Spline(x, y);
      return function(P) {
        return s.at(Math.max(0, Math.min(P / Pme, 1.2)));
      };
    },
    handleTermsAccepted: function() {
      this.display.accepted = true;
      if (this.loadedProject) {
        this.display.step = 4;
        this.display.tab = 1;
        this.changedSinceSave = false;
      }
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    },
    handleShipTypeChange: function(shipType) {
      if (shipType === "tanker") {
        this.project.ship = JSON.parse(JSON.stringify(ships.tanker1));
        this.project.rotors = ships.tanker1.defaultRotors;
        delete this.project.ship.defaultRotors;
      } else if (shipType === "bulk carrier") {
        this.project.ship = JSON.parse(JSON.stringify(ships.bulkCarrier1));
        this.project.rotors = ships.bulkCarrier1.defaultRotors;
      } else if (shipType === "ro-ro") {
        this.project.ship = JSON.parse(JSON.stringify(ships.roro1));
        this.project.rotors = JSON.parse(
          JSON.stringify(ships.roro1.defaultRotors)
        );
      }
      delete this.project.ship.defaultRotors;
      this.display.step = 0;
    },
    handleReverse: function() {
      this.project.route.waypoints = this.project.route.waypoints.reverse();
    },
    getColor: function(value) {
      return colors(value);
    },
    routeSelect: function(event) {
      console.log(event);
      if (this.routes[event.target.value]) {
        this.project.route.selectedRoute = event.target.value;
        this.project.route.waypoints = JSON.parse(
          JSON.stringify(
            this.routes[this.project.route.selectedRoute].waypoints
          )
        );
      }
    },
    estimateParticulars: function(onSuccess = function() {}) {
      var self = this;
      axios
        .post(API_URL + "particulars", {
          ship: this.project.ship
        })
        .then(function(response) {
          console.log(response.data);
          self.project.ship.particulars.S = response.data.S;
          self.project.ship.particulars.iE = response.data.i_E;
          //self.results.performance = response.data;
          onSuccess();
        })
        .catch(function(err) {
          console.log("Error unable to get particulars estimate", err);
        });
    },
    estimatePowering: function(onSuccess = function() {}) {
      var self = this;
      axios
        .post(API_URL + "speed-power", this.project.ship)
        .then(function(response) {
          console.log(response.data);
          self.project.ship.speedPower = response.data;
          onSuccess();
        })
        .catch(function(err) {
          console.log("Error unable to get Speed-Power", err);
        });
    },
    estimatePropeller: function(onSuccess = function() {}) {
      var self = this;
      axios
        .post(API_URL + "propeller", this.project.ship)
        .then(function(response) {
          console.log(response.data);
          self.project.ship.propeller.curves = response.data;
          onSuccess();
        })
        .catch(function(err) {
          console.log("Error unable to get propeller curves", err);
        });
    },
    estimateSFOC: function(onSuccess = function() {}) {
      this.project.ship.sfoc.mainEngine = [
        0.0,
        0.2,
        0.4,
        0.6,
        0.8,
        1.0,
        1.2
      ].map(x => ({ load: x.toFixed(1), sfoc: this.sfocAtLoad(x).toFixed(1) }));
      onSuccess();
    },
    handleFlettner: function() {
      var self = this;
      self.results.performance = null;
      self.results.calculating = true;
      axios
        .post(API_URL + "flettner", {
          ship: this.project.ship,
          flettner: this.project.rotors,
          speed: this.project.ship.particulars.Vmax,
          draught: this.project.route.draught
        })
        .then(function(response) {
          console.log(response.data);
          self.results.performance = response.data;
          self.results.calculating = false;
        })
        .catch(function(err) {
          console.log("Error unable to get flettner", err);
          self.results.calculating = false;
        });
    },
    onSaveProject: function() {
      var self = this;
      if (this.appMode || !this.changedSinceSave) {
        self.display.tab = 2;
        return;
      }
      var projectName = window.location.pathname.replace("/", "");
      axios
        .post(API_URL + "save", {
          project: this.project,
          previousSave: projectName
        })
        .then(function(response) {
          window.history.pushState(null, null, response.data.saveName);
          self.display.tab = 2;
          self.changedSinceSave = false;
        })
        .catch(function(err) {
          console.log("Error unable to save project", err);
        });
    },
    onLoadProject: function(projectName) {
      this.loadedProject = true;
      var self = this;
      axios
        .get(API_URL + "load/" + projectName)
        .then(function(response) {
          self.project = response.data;
          self.changedSinceSave = false;
          self.shipSelected = true;
          self.handleFlettner();
          console.log("Project data loaded");
        })
        .catch(function(err) {
          console.log("Error project data", err);
        });
    },
    sfocAtLoad: function(load) {
      return this.sfoc * (0.455 * load ** 2 - 0.71 * load + 1.28);
    },
    handleStepChange: function(dir) {
      var self = this;
      if (this.display.step == 0) {
        self.estimateParticulars(function() {
          self.display.step = 1;
        });
      } else if (this.display.step == 1) {
        if (dir === 1) {
          self.estimatePowering(function() {
            self.display.step = 2;
          });
        } else {
          self.display.step = 0;
        }
      } else if (this.display.step == 2) {
        if (dir === 1) {
          self.estimatePropeller(function() {
            self.display.step = 3;
          });
        } else {
          self.project.ship.speedPower = [];
          self.display.step = 1;
        }
      } else if (this.display.step == 3) {
        if (dir === 1) {
          self.estimateSFOC(function() {
            self.display.step = 4;
          });
        } else {
          self.project.ship.propeller.curves = [];
          self.display.step = 2;
        }
      } else if (this.display.step == 4) {
        if (dir === 1) {
          this.project.route.speed =
          Math.round(0.75 * this.project.ship.particulars.Vmax * 2) / 2; // rounds to the nearest 0.5
          this.project.route.draught = this.project.ship.particulars.T;
          this.handleFlettner();
          self.display.tab = 1;
          self.maxTab = 1;
        } else {
          self.project.ship.sfoc.mainEngine = [];
          self.display.step = 3;
        }
      }
    },
    handleSelectShip: function(ship, customize) {
      this.project.ship = JSON.parse(JSON.stringify(ship));
      this.project.rotors = ship.defaultRotors;
      delete this.project.ship.defaultRotors;
      this.shipSelected = true;
      if (customize) {
        this.display.step = 0;
        this.display.maxTab = 0;
      } else {
        var self = this;
        this.project.route.speed =
        Math.round(0.75 * this.project.ship.particulars.Vmax * 2) / 2; // rounds to the nearest 0.5
        this.project.route.draught = this.project.ship.particulars.T;
        self.estimateParticulars(function() {
          self.estimatePowering(function() {
            self.estimatePropeller(function() {
              self.estimateSFOC(function() {
                self.handleFlettner();
                self.display.tab = 1;
                self.display.maxTab = 1;
              });
            });
          });
        });
      }
      console.log("app", ship, customize);
    },
    handleRouteChange: function() {
      this.project.route.selectedRoute = "Customised";
    }
  },
  created() {
    this.$options.windData = null;
    if (window.location.pathname.replace("/", "") && !this.appMode) {
      this.onLoadProject(
        window.location.pathname.replace("/", "").replace("#", "")
      );
    }
  },
  mounted() {
    var self = this;
    axios
      .get(process.env.BASE_URL + "wind_data.bin", {
        responseType: "arraybuffer"
      })
      .then(function(response) {
        self.$options.windData = new Float32Array(response.data);
        self.windRange = 144 * 72;
        console.log("Wind data loaded");
      })
      .catch(function(err) {
        console.log("Error loading wind data", err);
      });
  },
  watch: {
    project: {
      handler(val) {
        this.changedSinceSave = true;
      },
      deep: true
    }
  }
};
</script>

<style>
@font-face {
  font-family: "Calmetta";
  src: url("./assets/Calmetta_Regular.woff2") format("woff2");
  font-weight: normal;
}
@font-face {
  font-family: "Calmetta";
  src: url("./assets/Calmetta_Bold.woff2") format("woff2");
  font-weight: bold;
}
@font-face {
  font-family: "Calmetta";
  src: url("./assets/Calmetta_Light.woff2") format("woff2");
  font-weight: lighter;
}

@font-face {
  font-family: "Source Sans Pro";
  src: url("./assets/SourceSansPro-Light-Italic.woff2") format("woff2");
  font-weight: lighter;
  font-style: italic;
}
@font-face {
  font-family: "Source Sans Pro";
  src: url("./assets/SourceSansPro-Regular.woff2") format("woff2");
  font-weight: normal;
}

body,
html {
  height: 100%;
  margin: 0;
}
#app .bg-light {
  background-color: #fff !important;
}
#app {
  font-family: "Source Sans Pro", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  /* margin-top: 60px; */
  height: 100%;
}
#app .nav-tabs .nav-link {
  font-size: 18px;
  border: none;
}
#app .nav-tabs .nav-link:hover {
  border-bottom: 4px solid #3b8ede;
}
#app .nav-link.active {
  border-bottom: 4px solid #3b8ede;
}
#app h2 {
  line-height: 1.26667;
  font-size: 3.6rem;
  font-weight: 700;
}
#app .underline {
  display: inline;
  position: relative;
  top: 0;
  background-image: linear-gradient(90deg, #3b8ede 0, #3b8ede);
  background-position: 0 100%;
  background-size: 0.1rem 0.3rem;
  background-repeat: repeat-x;
  font-family: Calmetta, Arial, Verdana, sans-serif;
}
.modal-backdrop {
  background-color: #00000070 !important;
}
#app button {
  font-size: 11pt;
  font-family: Calmetta, Arial, Verdana, sans-serif;
  font-weight: normal;
  background-color: #fff;
  color: #3b8ede;
  border: 2px solid #3b8ede;
  padding: 1px 14px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
#app button:enabled:hover {
  background-color: #3b8ede;
  color: #fff;
}
#app button:disabled {
  color: #999999;
  border: 2px solid #999999;
}

#app .fill {
  top: 132px;
  left: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  width: auto;
  height: auto;
}
#app td {
  padding: 2px 5px;
}
#app .rotor-button {
  width: 22%;
  margin: 0.5% 1.5%;
}
#app .rotor-button.active {
  background-color: #3b8ede;
  color: #fff;
}
</style>
